import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { Euro, Users, CalendarDays, Inbox, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useApi, formatEuro, formatDate, CATEGORY_LABELS } from "@/lib/api";
import { useMe, GoldSignIn } from "@/components/Nav";

function Console() {
  const api = useApi();
  const qc = useQueryClient();
  const [tab, setTab] = useState("applications");

  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data });
  const { data: apps = [] } = useQuery({ queryKey: ["admin-apps"], queryFn: async () => (await api.get("/admin/applications")).data });
  const { data: evenings = [] } = useQuery({ queryKey: ["admin-evenings"], queryFn: async () => (await api.get("/admin/evenings")).data });
  const { data: bookings = [] } = useQuery({ queryKey: ["admin-bookings"], queryFn: async () => (await api.get("/admin/bookings")).data });

  const decide = async (id, status) => {
    try {
      await api.put(`/admin/applications/${id}`, { status });
      toast.success(`Application ${status}`);
      qc.invalidateQueries({ queryKey: ["admin-apps"] });
    } catch { toast.error("Could not update"); }
  };

  const tabs = [["applications", "Applications", apps.length], ["evenings", "Evenings", evenings.length], ["bookings", "Bookings", bookings.length]];

  return (
    <div data-testid="admin-console">
      <div className="eyebrow mb-3">Admin</div>
      <h1 className="font-serif text-4xl lg:text-5xl text-wine mb-10">The console</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat icon={Euro} label="Revenue" value={formatEuro(stats?.revenue || 0)} />
        <Stat icon={Users} label="Seats sold" value={stats?.seats_sold ?? 0} />
        <Stat icon={CalendarDays} label="Evenings" value={stats?.evenings ?? 0} />
        <Stat icon={Inbox} label="Pending apps" value={stats?.pending_applications ?? 0} />
      </div>

      <div className="flex gap-2 mb-6 border-b border-stone-border">
        {tabs.map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
            className={`px-5 py-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${tab === key ? "border-gold text-wine" : "border-transparent text-stone hover:text-ink"}`}>
            {label} <span className="text-xs text-stone">({count})</span>
          </button>
        ))}
      </div>

      {tab === "applications" && (
        <div className="space-y-4" data-testid="admin-applications">
          {apps.length === 0 ? <Empty text="No applications yet." /> : apps.map((a) => (
            <div key={a.id} className="card-parchment p-6" data-testid={`application-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl text-ink">{a.venue_name}</h3>
                  <p className="text-sm text-ink-muted">{a.host_name} · {a.location} · {a.format}</p>
                  <p className="text-sm text-stone mt-1">{a.email}</p>
                  {a.message && <p className="text-sm text-ink-muted mt-3 italic">"{a.message}"</p>}
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={a.status} />
                </div>
              </div>
              {a.status === "pending" && (
                <div className="flex gap-3 mt-5">
                  <button onClick={() => decide(a.id, "approved")} data-testid={`approve-${a.id}`} className="btn-gold px-5 py-2 text-xs font-semibold tracking-wide">Approve</button>
                  <button onClick={() => decide(a.id, "declined")} data-testid={`decline-${a.id}`} className="px-5 py-2 text-xs tracking-wide border border-stone-border text-ink-muted hover:border-wine transition-colors">Decline</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "evenings" && (
        <div className="space-y-3" data-testid="admin-evenings">
          {evenings.length === 0 ? <Empty text="No evenings." /> : evenings.map((e) => (
            <div key={e.id} className="card-parchment p-5 flex items-center justify-between gap-4" data-testid={`admin-evening-${e.id}`}>
              <div>
                <span className="eyebrow">{CATEGORY_LABELS[e.category]} · {formatDate(e.date)}</span>
                <h3 className="font-serif text-xl text-ink mt-1">{e.title}</h3>
                <p className="text-sm text-ink-muted">{e.venue_name} · {e.host_name}</p>
              </div>
              <div className="text-right text-sm">
                <p className="text-wine font-medium">{e.seats_booked}/{e.seats_total} seats</p>
                <p className="text-stone">{formatEuro(e.price)}/seat</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-3" data-testid="admin-bookings">
          {bookings.length === 0 ? <Empty text="No bookings yet." /> : bookings.map((b) => (
            <div key={b.id} className="card-parchment p-5 flex items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-xl text-ink">{b.evening_title}</h3>
                <p className="text-sm text-ink-muted">{b.user_name} · {b.user_email}</p>
              </div>
              <div className="text-right text-sm">
                <p className="text-wine font-medium">{formatEuro(b.amount)}</p>
                <p className="text-stone">{b.seats} seat{b.seats > 1 ? "s" : ""}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-parchment p-5">
      <Icon className="w-5 h-5 text-gold mb-3" />
      <div className="font-serif text-3xl text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wide text-stone mt-1">{label}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const map = { pending: "bg-stone/20 text-stone", approved: "bg-wine text-parchment", declined: "bg-ink/10 text-ink-muted" };
  return <span className={`text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1 ${map[status]}`}>{status}</span>;
}
function Empty({ text }) {
  return <div className="card-parchment p-12 text-center text-ink-muted">{text}</div>;
}

export default function AdminDashboard() {
  const { isLoaded, isSignedIn } = useUser();
  const { data: me, isLoading } = useMe();

  return (
    <div data-testid="admin-page" className="pt-32 pb-24 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        {!isLoaded ? null : (
          <>
            <SignedOut>
              <Gate icon={Lock} title="Admin access" text="Sign in to continue." />
            </SignedOut>
            <SignedIn>
              {isLoading ? <div className="text-center py-20 text-stone">Checking access…</div> :
                me?.is_admin ? <Console /> :
                <Gate icon={ShieldAlert} title="Not authorised" text="This console is for VIVANT administrators only. Ask an admin to grant your account access (ADMIN_EMAILS)." />}
            </SignedIn>
          </>
        )}
      </div>
    </div>
  );
}

function Gate({ icon: Icon, title, text }) {
  return (
    <div className="text-center max-w-md mx-auto card-parchment p-12">
      <Icon className="w-10 h-10 text-gold mx-auto" />
      <h2 className="font-serif text-3xl text-wine mt-6">{title}</h2>
      <p className="text-ink-muted mt-2 mb-8">{text}</p>
      <GoldSignIn><button className="btn-gold px-8 py-3 text-sm font-semibold tracking-wide" data-testid="admin-gate-signin">Sign in</button></GoldSignIn>
    </div>
  );
}
